import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { of } from "rxjs";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { ClinicianDetailsActions } from "@store/actions/clinician-details/clinician-details.actions";
import { ClinicianDetailsService } from "@services/clinician-details.service";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { Router } from "@angular/router";
import { EditAddUserModalComponent } from "@components/modals/edit-add-user-modal/edit-add-user-modal.component";
import { UserAdminService } from "@services/user-admin.service";

@Injectable()
export class ClinicianDetailsEffects {

  private convertToNumber(value: string | number | undefined): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseInt(value, 10) || 0;
    return 0;
  }

  getOrgProviders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.getOrgProviders),
      withLatestFrom(this.store.select(selectOrgId)),      
      switchMap(([action, id]) => {
        return this.clinicianDetailsService
          .getProviders(this.convertToNumber(id))
          .pipe(
            map((data) =>
              ClinicianDetailsActions.getOrgProvidersSuccess({ response: data })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.getOrgProvidersFailure({ error: error.message || 'Failed to get providers' }))
            )
          );
      })
    );
  });

  createProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.createProvider),
      switchMap((action) => {
        return this.clinicianDetailsService
          .createProvider(action.providerData)
          .pipe(
            map((providerId) =>
              ClinicianDetailsActions.createProviderSuccess({ providerId })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.createProviderFailure({ error: error.message || 'Failed to create provider' }))
            )
          );
      })
    );
  });

  createProviderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.createProviderSuccess),
      map(() => ClinicianDetailsActions.getOrgProviders())
    );
  });

  updateProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.updateProvider),
      switchMap((action) => {
        return this.clinicianDetailsService
          .updateProvider(this.convertToNumber(action.providerId), action.providerData)
          .pipe(
            map((updatedProvider) =>
              ClinicianDetailsActions.updateProviderSuccess({ 
                providerId: this.convertToNumber(action.providerId), 
                updatedProvider 
              })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.updateProviderFailure({ 
                error: error.message || 'Failed to update provider' 
              }))
            )
          );
      })
    );
  });

  updateProviderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.updateProviderSuccess),
      map(() => ClinicianDetailsActions.getOrgProviders())
    );
  });

  deactivateProvider$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClinicianDetailsActions.deactivateProvider),
        tap((action) => {
          const dialogRef = this.dialog.open(ConfirmationModalComponent, {
            data: {
              title: "Deactivate Provider",
              message: `Are you sure you want to deactivate ${action.name}?`,
              confirmText: "Deactivate",
              cancelText: "Cancel"
            },
          });

          dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
              this.store.dispatch(
                ClinicianDetailsActions.confirmDeactivateProvider({ 
                  organizationProviderId: action.organizationProviderId 
                })
              );
            }
          });
        })
      ),
    { dispatch: false }
  );

  confirmDeactivateProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.confirmDeactivateProvider),
      switchMap((action) => {
        return this.clinicianDetailsService
          .deactivateProvider(action.organizationProviderId)
          .pipe(
            map(() =>
              ClinicianDetailsActions.deactivateProviderSuccess({ 
                organizationProviderId: action.organizationProviderId 
              })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.deactivateProviderFailure({ 
                error: error.message || 'Failed to deactivate provider' 
              }))
            )
          );
      })
    );
  });

  deactivateProviderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.deactivateProviderSuccess),
      map(() => ClinicianDetailsActions.getOrgProviders())
    );
  });

  createUserFromProviderPressed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClinicianDetailsActions.createUserFromProviderPressed),
        tap((action) => {
          this.dialog.open(EditAddUserModalComponent, {
            data: {
              title: "Create User",
              user: action.providerData,
              shouldPopulatedFieldsBeDisabled: true,
              organizationProviderId: action.organizationProviderId
            },
          });
        })
      ),
    { dispatch: false }
  );

  createUserFromProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.createUserFromProvider),
      switchMap((action) => {
        return this.userAdminService
          .addUser(action.userData)
          .pipe(
            map((userData) =>
              ClinicianDetailsActions.createUserFromProviderSuccess({ 
                userId: this.convertToNumber(userData.id),
                organizationProviderId: action.organizationProviderId
              })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.createUserFromProviderFailure({ error: error.message || 'Failed to create user' }))
            )
          );
      })
    );
  });

  createUserFromProviderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.createUserFromProviderSuccess),
      switchMap((action) => {
        return this.clinicianDetailsService
          .linkUserToProvider(action.organizationProviderId, action.userId)
          .pipe(
            map(() =>
              ClinicianDetailsActions.linkUserToProviderSuccess({ 
                userId: action.userId,
                organizationProviderId: action.organizationProviderId
              })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.linkUserToProviderFailure({ error: error.message || 'Failed to link user to provider' }))
            )
          );
      })
    );
  });
  linkUserToProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.linkUserToProvider),
      switchMap((action) => {
        return this.clinicianDetailsService
          .linkUserToProvider(action.organizationProviderId, action.userId)
          .pipe(
            map(() =>
              ClinicianDetailsActions.linkUserToProviderSuccess({ 
                userId: action.userId,
                organizationProviderId: action.organizationProviderId
              })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.linkUserToProviderFailure({ error: error.message || 'Failed to link user to provider' }))
            )
          );
      })
    );
  });

  linkUserToProviderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.linkUserToProviderSuccess),
      map(() => ClinicianDetailsActions.getOrgProviders())
    );
  });

  unlinkUserFromProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.unlinkUserFromProvider),
      switchMap((action) => {
        return this.clinicianDetailsService
          .unlinkUserFromProvider(action.organizationProviderId)
          .pipe(
            map(() =>
              ClinicianDetailsActions.unlinkUserFromProviderSuccess({ organizationProviderId: action.organizationProviderId })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.unlinkUserFromProviderFailure({ error: error.message || 'Failed to unlink user from provider' }))
            )
          );
      })
    );
  });

  unlinkUserFromProviderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.unlinkUserFromProviderSuccess),
      map(() => ClinicianDetailsActions.getOrgProviders())
    );
  });

  getProviderTaxonomies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClinicianDetailsActions.getProviderTaxonomies),
      switchMap(() => {
        return this.clinicianDetailsService
          .getProviderTaxonomies()
          .pipe(
            map((taxonomies) =>
              ClinicianDetailsActions.getProviderTaxonomiesSuccess({ taxonomies })
            ),
            catchError((error) =>
              of(ClinicianDetailsActions.getProviderTaxonomiesFailure({ 
                error: error.message || 'Failed to get provider taxonomies' 
              }))
            )
          );
      })
    );
  });

  uploadProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClinicianDetailsActions.uploadProviders),
      switchMap(({ file, organizationId }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('organizationId', organizationId);
        
        return this.clinicianDetailsService.uploadProviders(formData).pipe(
          map((results) => 
            ClinicianDetailsActions.uploadProvidersSuccess({ results })
          ),
          catchError((error) => {
            const errorMessage = error?.error?.message || 'Upload failed. Please try again.';
            return of(ClinicianDetailsActions.uploadProvidersFailure({ error: errorMessage }));
          })
        );
      })
    )
  );

  uploadProvidersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClinicianDetailsActions.uploadProvidersSuccess),
      map(() => ClinicianDetailsActions.getOrgProviders())
    )
  );

  constructor(
    private actions$: Actions,
    private clinicianDetailsService: ClinicianDetailsService,
    private userAdminService: UserAdminService,
    private dialog: MatDialog,
    private store: Store,
    private router: Router
  ) {}
}